import {
  DropdownMenu as RadixDropdownMenu,
  DropdownMenuContent,
  Portal,
  DropdownMenuContentProps,
  Item,
  DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu';
import { ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { DropdownOption, Option } from '@anytag/shared-types';
import { cn } from '@anytag/shared-utils';
import { TextCutter } from '../TextCutter';
import { MultiLevelMenu } from './MultiLevelMenu';
import { DropdownDefaultProps } from './types';

export interface DropdownMenuProps<T extends DropdownOption = DropdownOption>
  extends DropdownDefaultProps<T>,
    DropdownMenuContentProps {
  onSelectOption?: (option: T) => void;
  trigger: ReactNode;
  withinPortal?: boolean;
  withBorder?: boolean;
  open?: boolean;
  disabled?: boolean;
}

export const DropdownMenu = <T extends DropdownOption>({
  options,
  className,
  align = 'start',
  side = 'bottom',
  sideOffset = 4,
  onSelectOption,
  trigger,
  withinPortal = true,
  withBorder = true,
  disabled,
  open,
  onInteractOutside
}: DropdownMenuProps<T>) => {
  const MenuWrapper = withinPortal ? Portal : Slot;

  return (
    <RadixDropdownMenu modal={withinPortal} open={open}>
      <DropdownMenuTrigger asChild className={cn(disabled && 'pointer-events-none')}>
        {trigger}
      </DropdownMenuTrigger>
      <MenuWrapper>
        <DropdownMenuContent
          className={cn(
            'z-10 box-border w-max max-w-[350px] min-w-[165px] rounded bg-white p-2',
            withBorder ? 'border-base-border' : 'shadow-[0px_1px_2px_rgba(39,49,59,0.10)]',
            className
          )}
          sideOffset={sideOffset}
          align={align}
          side={side}
          onInteractOutside={onInteractOutside}
        >
          {options.map((option, index) => {
            if (option.hidden) return null;
            const { label, gtmId, active, disabled, className, onClick } = option;

            return option.menuType === 'multi-level' ? (
              <MultiLevelMenu
                key={index}
                value={option.value}
                label={option.label}
                submenu={option.submenu}
                onClickSubmenu={(o) => {
                  if (onClick) {
                    onClick(o);
                  } else {
                    onSelectOption?.(o as T);
                  }
                }}
                className={option.className}
              />
            ) : (
              <Item
                id={gtmId}
                key={index}
                className={cn(
                  'flex items-center gap-2 rounded bg-white p-2.5 text-sm transition-colors duration-100 outline-none',
                  !disabled && 'cursor-pointer hover:bg-menu-item-hover',
                  active && 'bg-active',
                  disabled && 'cursor-default',
                  className
                )}
                onClick={() =>
                  !disabled ? (onClick ? onClick(option as Option) : onSelectOption?.(option as T)) : undefined
                }
              >
                {option.prefixIcon}
                {typeof label === 'string' ? <TextCutter text={label} /> : label}
              </Item>
            );
          })}
        </DropdownMenuContent>
      </MenuWrapper>
    </RadixDropdownMenu>
  );
};
